import type { Schema } from '@/api/client'

export type Schedule = Schema<'ScheduleOut'>

/** The times the design offers. A closed list rather than a time input, because
 *  `app/runs/cron.py` has no free-text cron field and this is the other end of
 *  that decision — see its docstring on what a tenant-typed cron would cost.
 *
 *  **SEO-UI-109: four, in clock order, where the design draws three in
 *  04:00 / 02:00 / 22:00 order. Kept, deliberately.** The prototype's first
 *  option is its *default*, which is what puts 04:00 above 02:00 — an ordering
 *  that only makes sense in a static drawing. Here the selection comes off
 *  `schedule.hour`, so the list's order decides nothing except how hard it is
 *  to find a time in it, and a picker of clock times not in clock order is a
 *  thing a reader has to scan twice. 06:00 is the fourth because `cron.py`
 *  admits it and a tenant wanting the pass done before the working day is the
 *  case the other three do not cover. */
export const TIMES = ['02:00', '04:00', '06:00', '22:00'] as const

export const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const

export function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** *IST*, as `seo.html#triggers` prints the organisation's zone; any other zone by name. */
export function zoneName(timezone: string): string {
  return timezone === 'Asia/Kolkata' ? 'IST' : timezone.replace('_', ' ')
}

export function describeCadence(schedule: Schedule, product: string): string {
  if (!schedule.enabled) return `${product} will not run on its own for this site.`
  const at = `${pad(schedule.hour)}:${pad(schedule.minute)}`
  if (schedule.frequency === 'daily') return `${product} will run every day at ${at}.`
  if (schedule.frequency === 'weekdays') return `${product} will run on weekdays at ${at}.`
  const day = WEEKDAYS[(schedule.weekday ?? 1) - 1] ?? 'Monday'
  return `${product} will run every ${day} at ${at}.`
}

/** The `PUT /seo/run-schedules/{site_id}` body for a schedule as the form holds it. */
export function scheduleBody(body: Schedule): Schema<'ScheduleIn'> {
  return {
    enabled: body.enabled,
    frequency: body.frequency,
    hour: body.hour,
    minute: body.minute,
    // `?? null` rather than passing it through: the response type has the
    // column optional, the request type has it nullable-but-required, and
    // omitting it would let a schedule switched away from weekly keep a day.
    weekday: body.weekday ?? null,
    timezone: body.timezone,
    scope: body.scope,
    conditions: Object.fromEntries(
      body.conditions.map((condition) => [
        condition.name,
        { enabled: condition.enabled, threshold: condition.threshold },
      ]),
    ),
  }
}
